import type { UIConfigVariable } from 'types/ui-setup-config';
import { readVariableValue, variableFieldPath, paramKey } from 'context/StructureProvider/helper';
import { calculateInactiveInputValues, type InactiveCalcVariable, type UnitConversion } from './calculateInactiveInput';
import { getEvaluationOrder } from './evaluationOrder';

export type DeriveFailureReason = 'cycle' | 'calculation';

export type DeriveResult = {
  // Derived view of `values`. Same object as the input when nothing was derived.
  completedValues: Record<string, any>;
  // Base-unit results by paramKey, only for settled variables (scalar or table).
  baseValues: Map<string, number | number[][]>;
  // Paths whose calculation failed or is part of a cycle.
  failedPaths: Array<string>;
  // Why each failed path failed.
  failures: Map<string, DeriveFailureReason>;
};

const valuesEqual = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((row, rowIndex) => {
    const other = b[rowIndex];
    if (!Array.isArray(row) || !Array.isArray(other) || row.length !== other.length) return false;
    return row.every((cell, columnIndex) => Object.is(cell, other[columnIndex]));
  });
};

// Bridge, submit and import strip must all use this same definition.
export const getInactiveCalcVariables = (parameterList: ReadonlyArray<UIConfigVariable>): Array<UIConfigVariable> =>
  parameterList.filter((variable) => variable.inputType === 'InactiveCalc');

export const getDerivableInactiveCalcVariables = (
  parameterList: ReadonlyArray<UIConfigVariable>,
): Array<InactiveCalcVariable> =>
  getInactiveCalcVariables(parameterList).filter((variable): variable is InactiveCalcVariable =>
    Boolean(variable.calculation?.formula),
  );

// Only visible variables are unit-transformed for the form; hidden ones stay in the base unit.
export const toFormValueUnits = (
  parameterList: ReadonlyArray<UIConfigVariable>,
  visibleVariables: ReadonlyArray<Pick<UIConfigVariable, 'name' | 'element'>>,
): Array<UIConfigVariable> => {
  const visible = new Set(visibleVariables.map((variable) => paramKey(variable.element, variable.name)));
  return parameterList.map((variable) => {
    if (visible.has(paramKey(variable.element, variable.name))) return variable;
    const hidden = { ...variable, displayUnit: variable.unit };
    return variable.arrays ? { ...hidden, arrays: { ...variable.arrays, displayUnit: variable.arrays.unit } } : hidden;
  });
};

// Copy-on-write: never mutate the Formik values object.
/* eslint-disable no-param-reassign */
const writeDraft = (draft: Record<string, any>, element: string | undefined, name: string, value: unknown): void => {
  if (!element) {
    draft[name] = value;
    return;
  }
  draft[element] = { ...(draft[element] ?? {}) };
  draft[element][name] = value;
};
/* eslint-enable no-param-reassign */

/**
 * Derives all InactiveCalc values from `values` without writing to Formik.
 * Calculations are evaluated dependencies-first, so chains settle in one pass.
 * Members of a cycle are iterated in bounded passes and accepted once their
 * values stop changing; a cycle that does not settle fails as `cycle`.
 * A failed calculation keeps its last good value and is reported in
 * `failedPaths` with its reason in `failures`.
 */
export const deriveInactiveCalcValues = (
  values: Record<string, any>,
  inactiveCalcVariables: ReadonlyArray<InactiveCalcVariable>,
  parameterList: ReadonlyArray<UIConfigVariable>,
  unitConversion: UnitConversion,
  previousCompletedValues?: Record<string, any>,
): DeriveResult => {
  if (inactiveCalcVariables.length === 0) {
    return { completedValues: values, baseValues: new Map(), failedPaths: [], failures: new Map() };
  }

  const draft: Record<string, any> = { ...values };
  const baseValues = new Map<string, number | number[][]>();
  const failures = new Map<string, DeriveFailureReason>();

  const fail = (variable: InactiveCalcVariable, reason: DeriveFailureReason) => {
    failures.set(variableFieldPath(variable.element, variable.name), reason);
    const lastGood = previousCompletedValues
      ? readVariableValue(previousCompletedValues, variable.element, variable.name)
      : undefined;
    if (!valuesEqual(readVariableValue(draft, variable.element, variable.name), lastGood)) {
      writeDraft(draft, variable.element, variable.name, lastGood);
    }
  };

  // Evaluates one variable against the draft; returns whether the draft changed.
  const evaluate = (variable: InactiveCalcVariable): boolean => {
    const path = variableFieldPath(variable.element, variable.name);
    const before = readVariableValue(draft, variable.element, variable.name);
    const calculated = calculateInactiveInputValues(variable, draft, parameterList, unitConversion);
    if (!calculated) {
      baseValues.delete(paramKey(variable.element, variable.name));
      fail(variable, 'calculation');
    } else {
      failures.delete(path);
      baseValues.set(paramKey(variable.element, variable.name), calculated.baseValue);
      if (!valuesEqual(calculated.displayValue, before))
        writeDraft(draft, variable.element, variable.name, calculated.displayValue);
    }
    return !valuesEqual(readVariableValue(draft, variable.element, variable.name), before);
  };

  const evaluateCycle = (members: Array<InactiveCalcVariable>) => {
    const maxPasses = members.length + 1;
    let passChanged = false;
    for (let pass = 0; pass < maxPasses; pass += 1) {
      passChanged = false;
      for (const variable of members) {
        if (evaluate(variable)) passChanged = true;
      }
      if (!passChanged) return;
    }
    // Still moving after the bounded passes: the cycle diverges.
    members.forEach((variable) => {
      baseValues.delete(paramKey(variable.element, variable.name));
      fail(variable, 'cycle');
    });
  };

  getEvaluationOrder(inactiveCalcVariables, parameterList).forEach((group) => {
    if (group.isCycle) evaluateCycle(group.variables);
    else evaluate(group.variables[0]);
  });

  const changed = inactiveCalcVariables.some(
    (variable) =>
      !valuesEqual(
        readVariableValue(draft, variable.element, variable.name),
        readVariableValue(values, variable.element, variable.name),
      ),
  );

  // Declaration order, independent of the evaluation order.
  const failedPaths = inactiveCalcVariables
    .map((variable) => variableFieldPath(variable.element, variable.name))
    .filter((path) => failures.has(path));

  return {
    completedValues: changed ? draft : values,
    baseValues,
    failedPaths,
    failures,
  };
};

export default deriveInactiveCalcValues;
